// Unified event tracking helper (Step 37). Fires BOTH the first-party pipeline
// (POST /api/analytics/event -> Mongo analytics_events, powering the admin
// dashboard) AND the GTM dataLayer (-> GA4) from a single call, with an enriched
// payload. Generic by design: any button can be instrumented in one line via
// trackClick("my_button", { content_id }) without another build round.
import axios from "axios";
import { dataLayerAllowed } from "@/lib/consent";
import { syncConsentToAccount } from "@/lib/trackingContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function sessionId() {
  let sid = sessionStorage.getItem("samtwo_sid");
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("samtwo_sid", sid);
  }
  return sid;
}

function deviceType() {
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

// Visitor type: guest | subscriber | don. The app caches the current tier in
// localStorage (setViewerTier); falls back to token presence for subscriber.
function visitorType() {
  try {
    const tier = localStorage.getItem("samtwo_viewer_tier");
    if (tier === "premium" || tier === "don") return "don";
    if (tier === "regular" || tier === "boss") return "subscriber";
    if (localStorage.getItem("samtwo_platform_token")) return "subscriber";
  } catch {}
  return "guest";
}

export function setViewerTier(tier) {
  try {
    if (tier) localStorage.setItem("samtwo_viewer_tier", tier);
    else localStorage.removeItem("samtwo_viewer_tier");
  } catch {}
}

function utmParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    const out = {};
    ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
      const v = p.get(k);
      if (v) out[k] = v;
    });
    return out;
  } catch {
    return {};
  }
}

function enrichment() {
  return { session_id: sessionId(), visitor_type: visitorType(), device_type: deviceType(), ...utmParams() };
}

function push(payload) {
  if (!dataLayerAllowed()) return;
  try {
    window.dataLayer = window.dataLayer || [];
    if (payload.ecommerce) window.dataLayer.push({ ecommerce: null }); // clear the previous ecommerce object
    window.dataLayer.push({ page: window.location.pathname, ...enrichment(), ...payload });
  } catch {}
}

function sourceFromPath() {
  const seg = window.location.pathname.split("/").filter(Boolean)[0];
  return seg || "home";
}

// First-party event -> GA4-schema dataLayer event (or null = not routed). The first-party
// pipeline keeps the original names so the admin funnel is unaffected.
function toDataLayer(event, data) {
  if (event !== "button_click") return null;
  const b = data.button || "";
  if (b.startsWith("streaming_")) return { event: "streaming_click", platform: data.platform || b.slice(10), source: data.source || sourceFromPath(), content_id: data.content_id };
  if (b === "email_signup") return { event: "generate_lead", lead_type: "newsletter", source: data.source };
  if (b === "contest_cta") return { event: "contest_entry", contest_id: data.slug, contest_name: data.contest };
  if (data.content_type && data.content_id) return { event: "select_content", content_type: data.content_type, content_id: String(data.content_id), source: data.source };
  return null;
}

// Generic event tracker: first-party pipeline (unchanged names) + translated dataLayer event.
export function track(event, data = {}) {
  const page = window.location.pathname;
  const enriched = { ...enrichment(), ...data };
  axios.post(`${API}/analytics/event`, { event, page, data: enriched, session_id: enriched.session_id }).catch(() => {});
  const dl = toDataLayer(event, data);
  if (dl) push(dl);
}

// CTA click helper. `buttonName` is required; pass { content_type, content_id } when the
// click targets specific content (routes to GA4 select_content).
export function trackClick(buttonName, extra = {}) {
  track("button_click", { button: buttonName, button_name: buttonName, ...extra });
}

// dataLayer-only GA4 event (no first-party row). Consent-gated like everything else.
export function gaEvent(name, params = {}) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));
  push({ event: name, ...clean });
}

const cents = (c) => (c === undefined || c === null ? undefined : Math.round(Number(c)) / 100);

// GA4 item from our usual fields; price in cents.
export function gaItem({ id, name, category, priceCents, quantity = 1 }) {
  return { item_id: id ? String(id) : undefined, item_name: name, item_category: category, price: cents(priceCents), quantity };
}

// Ecommerce funnel events (view_item, add_to_cart, begin_checkout). valueCents omitted when unknown.
export function gaEcommerce(name, { items = [], valueCents, currency = "USD", ...extra } = {}) {
  const value = cents(valueCents);
  push({ event: name, ...extra, ecommerce: { currency: value === undefined ? undefined : currency, value, items } });
}

// Browser mirror of a server-confirmed purchase. eventId MUST equal the server's
// (subscription id for a new membership, PaymentIntent id otherwise) so Meta/TikTok
// de-duplicate. GA4 counts purchases from the server only: do not attach a GA4 tag to it.
export function trackPurchase({ eventId, category, purchaseType, items = [], valueCents, currency = "USD", tier, period }) {
  if (!eventId) return;
  const value = cents(valueCents);
  push({
    event: "purchase", event_id: eventId, item_category: category, purchase_type: purchaseType,
    ecommerce: { transaction_id: eventId, value, currency: value === undefined ? undefined : currency, items },
  });
  if (category === "membership") push({ event: "subscribe", event_id: eventId, tier, period, value, currency: value === undefined ? undefined : currency });
}

// Successful sign-in (any method). Also stores the visitor's cookie choice on the account.
export function trackLogin(method) {
  gaEvent("login", { method });
  syncConsentToAccount();
}

// Detail-page view. priceCents omitted when the item is not for sale.
export function trackViewItem({ id, name, category, priceCents }) {
  gaEcommerce("view_item", { items: [gaItem({ id, name, category, priceCents })], valueCents: priceCents || undefined, content_type: category, content_id: id ? String(id) : undefined });
}

export default track;
